import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, ArrowRight, Bookmark, Building2, CalendarClock, CircleDollarSign, CircleSlash, Eye,
  KanbanSquare, Landmark, NotebookPen, Plus, Search, TrendingUp, Trophy, UserRound, X,
} from "lucide-react";

import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import Loader from "@/components/ui/loader";
import { EmptyPanel, PageShell } from "@/components/interior-page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import AddDealModal from "@/components/AddDealModal";
import DealNotesDrawer from "@/components/DealNotesDrawer";
import { STAGES } from "@/lib/dealStages";
import {
  type BoardResponse, type DealBuyer, type DealCard, dealKey, fmtDate, fmtExact, fmtTotal,
  formatShortPrice, isMovable, isOverdue,
} from "@/lib/dealCrm";
import { cn } from "@/lib/utils";

/** Fold a stage the board no longer renders onto the one that replaced it.
 *
 *  'loi_accepted' was retired and its live rows folded back into
 *  'loi_submitted' by db/migrations/2026_09_07_remove_loi_accepted_stage.sql.
 *  Doing the same here covers the window between this build shipping and that
 *  migration running: without it a card left on the retired stage matches no
 *  column and simply disappears off the board.
 */
const boardStage = (slug: string): string =>
  STAGES.some((s) => s.slug === slug) ? slug : "loi_submitted";

export default function DealPipelineBoard() {
  const toast = useToast();

  const [board, setBoard] = useState<BoardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const [overCard, setOverCard] = useState<string | null>(null);

  // Filters — all applied server-side, so a board of thousands of interests
  // never has to be shipped whole to be searched.
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [advisorId, setAdvisorId] = useState("");
  const [includeClosed, setIncludeClosed] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [addOpen, setAddOpen] = useState(false);
  const [drawerCard, setDrawerCard] = useState<DealCard | null>(null);
  const [buyerCandidates, setBuyerCandidates] = useState<DealBuyer[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Only the newest request may write the board — a slow search response
  // must not land on top of a faster later one.
  const requestSeq = useRef(0);

  /** ``silent`` refreshes in place: it skips the full-page loader, which would
   *  otherwise unmount the board — and with it the drawer the user is still
   *  reading — and keeps the current board on failure rather than clearing it. */
  const loadBoard = useCallback(async (silent = false) => {
    const seq = ++requestSeq.current;
    if (silent) setRefreshing(true); else setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (advisorId) params.set("advisor_id", advisorId);
    if (includeClosed) params.set("include_closed", "true");
    for (const s of expanded) params.append("expand", s);
    const qs = params.toString();
    try {
      const data = await api.get<BoardResponse>(`/deal-pipeline/board${qs ? `?${qs}` : ""}`);
      if (seq === requestSeq.current) setBoard(data);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      if (!(e instanceof ApiError && e.status === 401)) {
        toast.error(e instanceof Error ? e.message : "Failed to load board");
      }
      if (!silent) setBoard(null);
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [debouncedSearch, advisorId, includeClosed, expanded, toast]);

  // First load shows the page loader; every filter change after that refreshes
  // in place so the toolbar keeps focus.
  const firstLoad = useRef(true);
  useEffect(() => {
    loadBoard(!firstLoad.current);
    firstLoad.current = false;
  }, [loadBoard]);

  const canEdit = !!board?.can_edit;
  const isStaff = !!board?.is_staff;
  const canNote = !!board?.can_note;
  const cards = useMemo(
    () => (board?.cards ?? []).map((c) => {
      const stage = boardStage(c.stage);
      return stage === c.stage ? c : { ...c, stage };
    }),
    [board],
  );

  const byStage = useMemo(() => {
    const map: Record<string, DealCard[]> = {};
    for (const s of STAGES) map[s.slug] = [];
    for (const c of cards) (map[c.stage] ??= []).push(c);
    return map;
  }, [cards]);

  const summary = board?.summary;
  const hasFilters = Boolean(debouncedSearch || advisorId || includeClosed);
  const replaceCard = useCallback((updated: DealCard) => {
    setBoard((b) => b ? { ...b, cards: b.cards.map((c) => dealKey(c) === dealKey(updated) ? updated : c) } : b);
  }, []);

  const moveTo = useCallback(async (card: DealCard, toStage: string, position?: number) => {
    if (!board || !isMovable(canEdit, card)) return;
    if (card.stage === toStage && position === undefined) return;
    const key = dealKey(card);
    const prev = board.cards;
    // Optimistic: reflect the new stage immediately.
    setBoard((b) => b ? { ...b, cards: b.cards.map((c) => dealKey(c) === key ? { ...c, stage: toStage } : c) } : b);
    try {
      await api.put<DealCard>("/deal-pipeline/cards", {
        // A listing-only deal has no buyer and is addressed by card_id instead.
        buyer_id: card.buyer?.id ?? null,
        card_id: card.card_id,
        listing_id: card.listing.listing_id,
        stage: toStage,
        position: position ?? null,
      });
      // Reload rather than patch: a move shifts the cards below it, changes
      // two columns' totals and the KPI tiles, all of which the server owns.
      loadBoard(true);
    } catch (e) {
      setBoard((b) => b ? { ...b, cards: prev } : b);
      toast.error(e instanceof Error ? e.message : "Failed to move deal");
    }
  }, [board, canEdit, loadBoard, toast]);

  const stepStage = (card: DealCard, dir: -1 | 1) => {
    const idx = STAGES.findIndex((s) => s.slug === card.stage);
    const next = STAGES[idx + dir];
    if (next) moveTo(card, next.slug);
  };

  const openDrawer = useCallback(async (card: DealCard) => {
    setDrawerCard(card);
    setBuyerCandidates([]);
    // A listing-only deal can be linked to any buyer interested in its
    // listing. The board may be truncated or filtered, so ask the server for
    // every deal on that listing rather than trusting what is on screen.
    if (card.source === "listing" && card.listing.reference_code) {
      try {
        const params = new URLSearchParams({ search: card.listing.reference_code, limit_per_stage: "500" });
        for (const s of STAGES) params.append("expand", s.slug);
        const data = await api.get<BoardResponse>(`/deal-pipeline/board?${params}`);
        const seen = new Set<string>();
        setBuyerCandidates(
          data.cards
            .filter((c) => c.listing.listing_id === card.listing.listing_id && c.buyer && !c.withdrawn)
            .map((c) => c.buyer!)
            .filter((b) => !seen.has(b.id) && seen.add(b.id)),
        );
      } catch {
        // No candidates is a safe fallback — the drawer says none have surfaced.
      }
    }
  }, []);

  // Keep the open drawer's card in step with board refreshes, matched by deal.
  useEffect(() => {
    if (!drawerCard || !board) return;
    const fresh = board.cards.find((c) => dealKey(c) === dealKey(drawerCard));
    if (fresh && fresh !== drawerCard && fresh.card_id !== drawerCard.card_id) setDrawerCard(fresh);
  }, [board, drawerCard]);

  const toggleExpanded = (slug: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });

  if (loading) return <Loader />;

  const total = summary?.total ?? 0;

  return (
    <PageShell>
      {/* Header — same Dark · Gold hero as the other interior pages */}
      <section className="public-hero-shell rounded-[1.85rem] px-6 py-7 shadow-glow sm:px-8">
        <div className="public-grid-lattice absolute inset-0 opacity-45" />
        <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
        <div className="relative z-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="public-pill">
              <KanbanSquare className="h-3.5 w-3.5" />
              Deal CRM
            </div>
            <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl">
              Deal <span className="text-gradient">Pipeline CRM</span>
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
              {total.toLocaleString()} deal{total === 1 ? "" : "s"} {includeClosed ? "on the board" : "in motion"} — {isStaff
                ? "move a deal forward as it progresses toward close."
                : canNote
                ? "follow each deal's stage as your BL advisor moves it forward, and keep private notes."
                : "tracked by your BL advisor from first look to close."}
            </p>
          </div>
          {isStaff && (
            <Button onClick={() => setAddOpen(true)} className="shrink-0">
              <Plus className="h-4 w-4" />
              Add deal
            </Button>
          )}
        </div>
      </section>

      {/* KPI tiles — over the whole filtered board, not just the cards shown */}
      {summary && summary.total > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:max-w-3xl">
          <div className="public-listing-card rounded-2xl p-4 shadow-glow">
            <div className="flex items-center justify-between">
              <p className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Pipeline Value</p>
              <CircleDollarSign className="h-4 w-4 text-primary/70" />
            </div>
            <p
              className="mt-1.5 text-2xl font-extrabold tracking-tight text-primary"
              title={`${fmtExact(summary.total_value)} across ${summary.valued_listings.toLocaleString()} listing${summary.valued_listings === 1 ? "" : "s"}`}
            >
              {fmtTotal(summary.total_value)}
            </p>
            <p className="mt-0.5 text-[0.7rem] text-muted-foreground">
              asking price across {summary.valued_listings.toLocaleString()} listing{summary.valued_listings === 1 ? "" : "s"}
            </p>
          </div>
          <div className="public-listing-card rounded-2xl p-4 shadow-glow">
            <div className="flex items-center justify-between">
              <p className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Active Deals</p>
              <TrendingUp className="h-4 w-4 text-primary/70" />
            </div>
            <p className="mt-1.5 text-2xl font-extrabold tracking-tight text-foreground tabular-nums">{summary.total.toLocaleString()}</p>
            <p className="mt-0.5 text-[0.7rem] text-muted-foreground">
              across {summary.stages_in_play} stage{summary.stages_in_play === 1 ? "" : "s"}
            </p>
          </div>
          <div className="public-listing-card rounded-2xl p-4 shadow-glow">
            <div className="flex items-center justify-between">
              <p className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">In Diligence or Later</p>
              <Landmark className="h-4 w-4 text-primary/70" />
            </div>
            <p className="mt-1.5 text-2xl font-extrabold tracking-tight text-foreground tabular-nums">{summary.late_stage.toLocaleString()}</p>
            <p className="mt-0.5 text-[0.7rem] text-muted-foreground">due diligence → closing</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {(total > 0 || hasFilters) && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isStaff ? "Search BL code, listing or buyer…" : "Search BL code or listing…"}
              className="pl-10 pr-9"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                title="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {(board?.advisors.length ?? 0) > 0 && (
            <SearchableSelect
              value={advisorId}
              onChange={setAdvisorId}
              placeholder="All advisors"
              options={[{ value: "", label: "All advisors" }, ...board!.advisors.map((a) => ({ value: a.id, label: a.name }))]}
              className="w-56"
            />
          )}
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            Show closed
          </label>
          {refreshing && <span className="text-xs text-muted-foreground">Updating…</span>}
        </div>
      )}

      {/* Board */}
      {total === 0 ? (
        <EmptyPanel
          icon={Building2}
          title={hasFilters ? "No matching deals" : "No deals yet"}
          description={hasFilters
            ? "Nothing on the board matches these filters."
            : isStaff
            ? "Deals appear here automatically when a buyer expresses interest in a listing you manage. You can also add one of your own listings with “Add deal” and start tracking it before a buyer surfaces."
            : "Deals appear here automatically once you save a listing or express interest in one."}
        />
      ) : (
        <div className="mt-4 flex gap-4 overflow-x-auto pb-4">
          {STAGES.map((stage) => {
            const columnCards = byStage[stage.slug] ?? [];
            const stats = board?.stages[stage.slug];
            const columnCount = stats?.count ?? columnCards.length;
            const columnRevenue = stats?.revenue ?? 0;
            const hidden = columnCount - columnCards.length;
            const isExpanded = expanded.has(stage.slug);
            return (
              <div
                key={stage.slug}
                onDragOver={(e) => { if (canEdit && draggingKey) { e.preventDefault(); setOverStage(stage.slug); } }}
                onDragLeave={() => setOverStage((s) => (s === stage.slug ? null : s))}
                onDrop={(e) => {
                  e.preventDefault();
                  setOverStage(null);
                  setOverCard(null);
                  if (!canEdit || !draggingKey) return;
                  const card = cards.find((c) => dealKey(c) === draggingKey);
                  setDraggingKey(null);
                  if (card) moveTo(card, stage.slug);
                }}
                className={cn(
                  // Mobile/tablet: fixed-width columns with horizontal scroll.
                  // Desktop (lg+): all six columns share the row equally so the
                  // whole board fits the viewport at 100% zoom — no sideways scroll.
                  "flex h-[calc(100dvh-14rem)] min-h-[22rem] w-[18.5rem] shrink-0 flex-col rounded-2xl border bg-card/50 p-3 transition-colors sm:w-80 lg:w-auto lg:min-w-0 lg:shrink lg:flex-1 lg:basis-0",
                  overStage === stage.slug ? "border-primary/60 bg-primary/5" : "border-border/70",
                )}
              >
                <div className="mb-3 flex shrink-0 items-center justify-between gap-1.5 px-1">
                  <span className={cn("flex min-w-0 items-center gap-1.5 text-[0.7rem] font-bold uppercase tracking-[0.14em]", stage.accent)}>
                    <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", stage.dot)} />
                    <span className="truncate">{stage.label}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {/* Combined revenue of the column, next to its deal count.
                        Hidden at zero: every listing in the column lacking a
                        revenue figure is not the same as "$0". */}
                    {columnRevenue > 0 && (
                      <span
                        className="text-[0.65rem] font-bold tabular-nums text-muted-foreground"
                        title={`${fmtExact(columnRevenue)} combined annual revenue`}
                      >
                        {fmtTotal(columnRevenue)}
                      </span>
                    )}
                    <span
                      className={cn("rounded-full px-2 py-0.5 text-[0.65rem] font-bold tabular-nums", stage.chip, stage.accent)}
                      title={`${columnCount.toLocaleString()} deal${columnCount === 1 ? "" : "s"}`}
                    >
                      {columnCount.toLocaleString()}
                    </span>
                  </span>
                </div>

                <div className="flex flex-1 flex-col gap-2.5 overflow-y-auto pr-0.5">
                  {columnCards.length === 0 && (
                    <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border/60">
                      <p className="px-4 text-center text-xs leading-5 text-muted-foreground/70">
                        {hasFilters
                          ? "No matching deals"
                          : stage.slug === "closing"
                          ? "No closing deals yet — keep pushing!"
                          : `No deals in ${stage.label.toLowerCase()} yet`}
                      </p>
                    </div>
                  )}
                  {columnCards.map((card) => {
                    const idx = STAGES.findIndex((s) => s.slug === card.stage);
                    const key = dealKey(card);
                    const movable = isMovable(canEdit, card);
                    const canOpen = isStaff || canNote;
                    return (
                      <div
                        key={key}
                        draggable={movable}
                        onDragStart={() => movable && setDraggingKey(key)}
                        onDragEnd={() => { setDraggingKey(null); setOverStage(null); setOverCard(null); }}
                        onDragOver={(e) => {
                          if (!canEdit || !draggingKey || draggingKey === key) return;
                          e.preventDefault();
                          setOverCard(key);
                        }}
                        onDragLeave={() => setOverCard((k) => (k === key ? null : k))}
                        onDrop={(e) => {
                          // Dropped ON a card: slot in before it, in its column.
                          e.preventDefault();
                          e.stopPropagation();
                          setOverStage(null);
                          setOverCard(null);
                          if (!canEdit || !draggingKey || draggingKey === key) return;
                          const dragged = cards.find((c) => dealKey(c) === draggingKey);
                          setDraggingKey(null);
                          if (dragged) moveTo(dragged, card.stage, card.position);
                        }}
                        onClick={() => canOpen && openDrawer(card)}
                        className={cn(
                          "rounded-xl border border-border bg-gradient-to-b from-background to-muted/30 p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md",
                          movable && "cursor-grab active:cursor-grabbing",
                          !movable && canOpen && "cursor-pointer",
                          draggingKey === key && "opacity-50",
                          overCard === key && "border-t-2 border-t-primary",
                          (card.outcome === "lost" || card.withdrawn) && "opacity-70",
                        )}
                      >
                        <div className="mb-1 flex items-start justify-between gap-2">
                          <div className="flex min-w-0 flex-wrap items-center gap-1">
                            {card.outcome === "won" && (
                              <span className="flex items-center gap-0.5 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[0.58rem] font-bold uppercase tracking-wider text-emerald-500">
                                <Trophy className="h-2.5 w-2.5" /> Won
                              </span>
                            )}
                            {card.outcome === "lost" && (
                              <span className="flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[0.58rem] font-bold uppercase tracking-wider text-muted-foreground">
                                <CircleSlash className="h-2.5 w-2.5" /> Lost
                              </span>
                            )}
                            {card.withdrawn && (
                              <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[0.58rem] font-bold uppercase tracking-wider text-amber-500">
                                Withdrawn
                              </span>
                            )}
                            {card.source === "favorite" && (
                              <span className="flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[0.58rem] font-bold uppercase tracking-wider text-primary" title="A listing you saved">
                                <Bookmark className="h-2.5 w-2.5" /> Saved
                              </span>
                            )}
                            {card.listing.industry_category && (
                              <p className="truncate text-[0.6rem] font-semibold uppercase tracking-wider text-primary/70">
                                {card.listing.industry_category}
                              </p>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            {canOpen && (
                              <button
                                type="button"
                                title={card.note_count > 0 ? `${card.note_count} note${card.note_count === 1 ? "" : "s"} & details` : "Notes & details"}
                                draggable={false}
                                onClick={(e) => { e.stopPropagation(); openDrawer(card); }}
                                onDragStart={(e) => e.stopPropagation()}
                                className={cn(
                                  "flex items-center gap-0.5 rounded-md p-0.5 transition-colors hover:bg-muted hover:text-primary",
                                  card.note_count > 0 ? "text-primary" : "text-muted-foreground/60",
                                )}
                              >
                                <NotebookPen className="h-3.5 w-3.5" />
                                {card.note_count > 0 && (
                                  <span className="text-[0.6rem] font-bold tabular-nums">{card.note_count}</span>
                                )}
                              </button>
                            )}
                            <a
                              href={`/listings/${card.listing.listing_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open listing in new tab"
                              draggable={false}
                              onClick={(e) => e.stopPropagation()}
                              onDragStart={(e) => e.stopPropagation()}
                              className="rounded-md p-0.5 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-primary"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </a>
                            <span className={cn("h-2 w-2 rounded-full", stage.dot)} title={stage.label} />
                          </div>
                        </div>
                        <p
                          className="line-clamp-2 text-sm font-semibold leading-snug text-foreground"
                          title={card.listing.title || "Untitled listing"}
                        >
                          {card.listing.title || "Untitled listing"}
                        </p>
                        <p className="mt-1 text-xs font-medium">
                          <span className="font-bold text-primary">{formatShortPrice(card.listing)}</span>
                          {card.offer_amount != null && (
                            <span className="ml-1.5 text-muted-foreground" title={`Offer ${fmtExact(card.offer_amount)}`}>
                              · offer {fmtTotal(card.offer_amount)}
                            </span>
                          )}
                          {card.listing.reference_code && (
                            <span className="ml-2 font-mono text-[0.65rem] text-muted-foreground/60">
                              {card.listing.reference_code}
                            </span>
                          )}
                        </p>

                        {/* Staff see whose deal this is (one listing can have many
                            buyers) and, on the admin board, whose desk it sits on. */}
                        {isStaff && (
                          <p className="mt-1.5 flex items-center gap-1 truncate text-[0.7rem] text-muted-foreground/80">
                            <UserRound className="h-3 w-3 shrink-0" />
                            {card.buyer ? (
                              <span className="truncate" title={card.buyer.email}>{card.buyer.first_name} {card.buyer.last_name}</span>
                            ) : (
                              <span className="italic text-muted-foreground/60">No buyer yet</span>
                            )}
                            {card.advisor && (board?.advisors.length ?? 0) > 0 && (
                              <span className="truncate text-muted-foreground/60">· {card.advisor.name}</span>
                            )}
                          </p>
                        )}

                        {card.next_step && (
                          <p
                            className={cn(
                              "mt-1.5 flex items-start gap-1 text-[0.7rem] leading-4",
                              isOverdue(card.next_step_due) && !card.outcome ? "text-red-500" : "text-muted-foreground",
                            )}
                            title={card.next_step_due ? `Due ${fmtDate(card.next_step_due)}` : undefined}
                          >
                            <CalendarClock className="mt-px h-3 w-3 shrink-0" />
                            <span className="line-clamp-2">
                              {card.next_step}
                              {card.next_step_due && ` · ${fmtDate(card.next_step_due)}`}
                            </span>
                          </p>
                        )}

                        {movable && (
                          <div className="mt-2.5 flex items-center gap-1.5">
                            <button
                              type="button"
                              title="Move back"
                              disabled={idx === 0}
                              onClick={(e) => { e.stopPropagation(); stepStage(card, -1); }}
                              className="flex flex-1 items-center justify-center rounded-md border border-border/70 bg-muted/40 py-1 text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <ArrowLeft className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              title="Move forward"
                              disabled={idx === STAGES.length - 1}
                              onClick={(e) => { e.stopPropagation(); stepStage(card, 1); }}
                              className="flex flex-1 items-center justify-center rounded-md border border-primary/30 bg-primary/10 py-1 text-primary transition-colors hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(hidden > 0 || isExpanded) && (
                    <button
                      type="button"
                      onClick={() => toggleExpanded(stage.slug)}
                      className="shrink-0 rounded-lg border border-dashed border-border/70 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                    >
                      {isExpanded ? "Show fewer" : `Show all ${columnCount.toLocaleString()}`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddDealModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => loadBoard(true)}
      />

      <DealNotesDrawer
        card={drawerCard}
        isStaff={isStaff}
        buyerCandidates={buyerCandidates}
        onClose={() => setDrawerCard(null)}
        onCardChanged={(c) => { replaceCard(c); setDrawerCard(c); }}
        onBoardChanged={() => loadBoard(true)}
      />
    </PageShell>
  );
}
